import clsx from 'clsx';
import { MouseEvent, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Popover, { PopoverProps } from '@mui/material/Popover';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';

import { filterBtnStyles, filtersStyles, tooltipStyles } from './styles';
import { TableFiltersState } from 'types/common';
import { ResetButton } from './ResetButton';
import Button from '@mui/material/Button';
import { shouldShowBadgeForFilters } from './utils';
import { TABLE_INITIAL_FILTERS } from './constants';
import { Form } from '../Form/Form';
import { FormDateRangePicker } from '../Form/DateRangePicker';
import { FormMultipleSelectCheckmarks } from '../Form/Select';
import { SelectOption } from 'types/form';
import Typography from '@mui/material/Typography';

interface Props {
    defaultValues?: TableFiltersState;
    statuses?: SelectOption[];
    onApplyFilters: (values: TableFiltersState) => void;
}

const popoverBaseProps: Partial<PopoverProps> = {
    anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
    },
    transformOrigin: {
        vertical: 'top',
        horizontal: 'right',
    },
};

export const TableFilters = ({ defaultValues = TABLE_INITIAL_FILTERS, statuses, onApplyFilters }: Props) => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    const isOpened = !!anchor;

    const hasFilters = useMemo(() => shouldShowBadgeForFilters(defaultValues), [defaultValues]);

    const handleClose = useCallback(() => {
        setAnchor(null);
        onApplyFilters(TABLE_INITIAL_FILTERS);
    }, [onApplyFilters]);

    const handleOpenFilters = useCallback(({ currentTarget }: MouseEvent<HTMLButtonElement>) => {
        setAnchor(currentTarget as HTMLElement);
    }, []);

    const handleCloseFilters = useCallback(() => {
        setAnchor(null);
    }, []);

    const handleSubmit = useCallback(
        (values: TableFiltersState) => {
            onApplyFilters(values);
            setAnchor(null);
        },
        [onApplyFilters],
    );

    useLayoutEffect(() => {
        if (anchor) {
            anchor.querySelector('input')?.focus();
        }
    }, [anchor]);

    return (
        <>
            <Button
                variant="outlined"
                startIcon={
                    <Badge color="error" variant="dot" invisible={!hasFilters}>
                        <FilterListIcon color="primary" />
                    </Badge>
                }
                endIcon={isOpened ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                className={clsx({ isOpened })}
                sx={filterBtnStyles}
                onClick={handleOpenFilters}
            >
                Filter
            </Button>
            <Popover
                {...popoverBaseProps}
                open={isOpened}
                onClose={handleCloseFilters}
                anchorEl={anchor}
                sx={tooltipStyles}
            >
                <Paper sx={filtersStyles}>
                    <Typography mb={1}>Select Filters</Typography>
                    <Form id="email-filters-form" initialValues={defaultValues} onSubmit={handleSubmit}>
                        <FormDateRangePicker />
                        {statuses && (
                            <FormMultipleSelectCheckmarks
                                name="status"
                                size="small"
                                label="Status"
                                options={statuses}
                            />
                        )}
                        <Box display="flex" justifyContent="flex-end" gap={1} mt={1.5}>
                            <ResetButton form="email-filters-form" variant="text" onClick={handleClose}>
                                Reset
                            </ResetButton>
                            <Button form="email-filters-form" type="submit">
                                Apply
                            </Button>
                        </Box>
                    </Form>
                </Paper>
            </Popover>
        </>
    );
};
